import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { RouterModule, Router } from '@angular/router';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { RegistroService } from 'app/services/registro.service';
import { NotificacaoService } from 'app/services/notificacao.service';
import { StorageService } from 'app/services/storage.service';
import { KeysStorage } from 'app/comum/keys-storage';
import { AnalyticsService } from 'app/services/analytics.service';
import { MarketingAttributionService } from 'app/services/marketing-attribution.service';
import { cpfValidator } from 'app/validators/cpf.validator';

@Component({
    selector: 'app-registro',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatButtonModule,
        RouterModule,
        NgxMaskDirective
    ],
    providers: [provideNgxMask()],
    templateUrl: './registro.component.html',
    styleUrls: ['./registro.component.scss']
})
export class RegistroComponent {
    private fb = inject(FormBuilder);
    private registroService = inject(RegistroService);
    private notificacaoService = inject(NotificacaoService);
    private storageService = inject(StorageService);
    private router = inject(Router);
    private analyticsService = inject(AnalyticsService);
    private marketingAttribution = inject(MarketingAttributionService);

    step: 'form' | 'verification' = 'form';
    loading = false;
    esconderSenha = true;
    esconderConfirmar = true;
    emailRegistrado = '';
    reenviarTimer = 0;
    private timerInterval: any;

    registroForm: FormGroup = this.fb.group({
        nome: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(300)]],
        email: ['', [Validators.required, Validators.email]],
        celular: ['', [Validators.required]],
        cpf: ['', [Validators.required, Validators.minLength(11), Validators.maxLength(11), cpfValidator]],
        senha: ['', [Validators.required, Validators.minLength(6)]],
        confirmarSenha: ['', [Validators.required]]
    });

    codigoForm: FormGroup = this.fb.group({
        codigo: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]]
    });

    async registrar() {
        if (this.registroForm.invalid) {
            this.registroForm.markAllAsTouched();
            return;
        }

        const values = this.registroForm.value;
        if (values.senha !== values.confirmarSenha) {
            this.notificacaoService.mensagemErro('As senhas não conferem.');
            return;
        }

        this.analyticsService.trackEvent('sign_up_start');
        this.loading = true;
        try {
            const resposta = await this.registroService.criar({
                nome: values.nome,
                email: values.email,
                celular: values.celular,
                cpf: values.cpf,
                senha: values.senha,
                confirmarSenha: values.confirmarSenha,
                gclid: this.marketingAttribution.getGclid()
            });

            if (resposta.sucesso) {
                this.emailRegistrado = values.email;
                this.step = 'verification';
                this.iniciarTimerReenvio();
                this.notificacaoService.mensagemSucesso('Código de verificação enviado para seu email!');
            } else {
                this.notificacaoService.mensagemErro(resposta.mensagem);
            }
        } catch (erro: any) {
            const msg = erro?.error?.mensagem || erro?.error || 'Erro ao criar conta. Tente novamente.';
            this.notificacaoService.mensagemErro(msg);
        } finally {
            this.loading = false;
        }
    }

    async validarEmail() {
        if (this.codigoForm.invalid) {
            this.codigoForm.markAllAsTouched();
            return;
        }

        this.loading = true;
        try {
            const retorno = await this.registroService.validarEmail({
                email: this.emailRegistrado,
                codigo: this.codigoForm.value.codigo
            });

            // Auto-login
            this.storageService.set(retorno.tokenJwt, KeysStorage.tokenJwt());
            this.storageService.set(String(retorno.primeiroAcesso), KeysStorage.primeiroAcesso());
            if (retorno.dataFimTrial) {
                this.storageService.set(retorno.dataFimTrial, KeysStorage.dataFimTrial());
            }
            this.analyticsService.trackEvent('sign_up');
            this.analyticsService.setUserProperties({ plan_type: 'trial' });
            await this.analyticsService.trackGoogleAdsConversion();
            this.notificacaoService.mensagemSucesso('Email verificado com sucesso! Bem-vindo ao Easy Consultório!');
            this.router.navigateByUrl('/painel');
        } catch (erro: any) {
            const msg = erro?.error?.mensagem || erro?.error || 'Código inválido ou expirado.';
            this.notificacaoService.mensagemErro(msg);
        } finally {
            this.loading = false;
        }
    }

    async reenviarCodigo() {
        if (this.reenviarTimer > 0) return;

        this.loading = true;
        try {
            const resposta = await this.registroService.reenviarCodigo(this.emailRegistrado);
            this.notificacaoService.mensagemSucesso(resposta.mensagem);
            this.iniciarTimerReenvio();
        } catch (erro: any) {
            this.notificacaoService.mensagemErro('Erro ao reenviar código. Tente novamente.');
        } finally {
            this.loading = false;
        }
    }

    private iniciarTimerReenvio() {
        this.reenviarTimer = 60;
        if (this.timerInterval) clearInterval(this.timerInterval);
        this.timerInterval = setInterval(() => {
            this.reenviarTimer--;
            if (this.reenviarTimer <= 0) {
                clearInterval(this.timerInterval);
            }
        }, 1000);
    }

    voltarParaFormulario() {
        this.step = 'form';
        this.codigoForm.reset();
    }

    ngOnDestroy() {
        if (this.timerInterval) clearInterval(this.timerInterval);
    }
}
